import { supabase } from './client';
import type { JobApplication, CreateJobApplicationData, UpdateJobApplicationData } from '../../types/applications';

export interface SupabaseJobApplication {
  id: string;
  job_id: string;
  applicant_id: string;
  message: string;
  status: string;
  resume_url?: string;
  voice_intro_url?: string;
  video_url?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

function convertSupabaseToApplication(supabaseApp: SupabaseJobApplication): JobApplication {
  return {
    id: supabaseApp.id,
    jobId: supabaseApp.job_id,
    applicantId: supabaseApp.applicant_id,
    message: supabaseApp.message,
    status: supabaseApp.status as JobApplication['status'],
    resumeUrl: supabaseApp.resume_url,
    voiceIntroUrl: supabaseApp.voice_intro_url,
    videoUrl: supabaseApp.video_url,
    notes: supabaseApp.notes,
    createdAt: supabaseApp.created_at,
    updatedAt: supabaseApp.updated_at
  };
}

export async function uploadResume(file: File, userId: string): Promise<{ url: string | null; error: Error | null }> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `resumes/${fileName}`;

    const { data, error } = await supabase.storage
      .from('job-applications')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('job-applications')
      .getPublicUrl(filePath);

    return { url: urlData.publicUrl, error: null };
  } catch (error) {
    return { url: null, error: error as Error };
  }
}

export async function uploadApplicationVideo(
  file: File,
  userId: string
): Promise<{ url: string | null; error: Error | null }> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-video-${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    const { data, error } = await supabase.storage
      .from('job-application-videos')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('job-application-videos')
      .getPublicUrl(filePath);

    return { url: urlData.publicUrl, error: null };
  } catch (error) {
    return { url: null, error: error as Error };
  }
}

export async function uploadApplicationVoiceRecording(
  audioBlob: Blob,
  applicationId: string
): Promise<{ url: string | null; error: Error | null }> {
  try {
    if (!audioBlob || audioBlob.size === 0) {
      throw new Error('Invalid audio file: file is empty');
    }

    if (audioBlob.size > 10 * 1024 * 1024) {
      throw new Error('Audio file is too large. Maximum size is 10MB');
    }

    const validBaseTypes = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/wav'];
    const baseType = audioBlob.type.split(';')[0];

    if (!validBaseTypes.includes(baseType)) {
      throw new Error(`Invalid audio format: ${baseType}. Supported formats: webm, mp4, ogg, wav`);
    }

    const uuid = crypto.randomUUID();
    const fileExtension = baseType.split('/')[1] || 'webm';
    const filePath = `${applicationId}/${uuid}.${fileExtension}`;

    const { data, error } = await supabase.storage
      .from('application-voice')
      .upload(filePath, audioBlob, {
        cacheControl: '3600',
        upsert: false,
        contentType: audioBlob.type
      });

    if (error) {
      if (error.message?.includes('already exists')) {
        throw new Error('Upload failed: file already exists. Please try again.');
      }
      throw error;
    }

    const { data: urlData } = supabase.storage
      .from('application-voice')
      .getPublicUrl(filePath);

    if (!urlData?.publicUrl) {
      throw new Error('Failed to generate public URL for uploaded file');
    }

    return { url: urlData.publicUrl, error: null };
  } catch (error) {
    console.error('Error uploading voice recording:', error);
    return {
      url: null,
      error: error instanceof Error ? error : new Error('Unknown error uploading voice recording')
    };
  }
}

export async function createJobApplication(applicationData: CreateJobApplicationData & { existingResumeUrl?: string }) {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    let resumeUrl: string | null = null;
    let videoUrl: string | null = null;

    if (applicationData.resumeFile) {
      const { url, error: uploadError } = await uploadResume(applicationData.resumeFile, user.id);
      if (uploadError) {
        throw new Error(`Failed to upload resume: ${uploadError.message}`);
      }
      resumeUrl = url;

      const { updateStoredResume } = await import('./subscriptions');
      await updateStoredResume(user.id, url);
    } else if (applicationData.existingResumeUrl) {
      resumeUrl = applicationData.existingResumeUrl;
    }

    if (applicationData.videoFile) {
      const { url, error: uploadError } = await uploadApplicationVideo(applicationData.videoFile, user.id);
      if (uploadError) {
        throw new Error(`Failed to upload video: ${uploadError.message}`);
      }
      videoUrl = url;
    }

    const { data, error } = await supabase
      .from('job_applications')
      .insert({
        job_id: applicationData.jobId,
        applicant_id: user.id,
        message: applicationData.message,
        status: 'pending',
        resume_url: resumeUrl,
        voice_intro_url: applicationData.voiceIntroUrl,
        video_url: videoUrl,
        notes: applicationData.notes || '',
        applicant_email: user.email,
        applicant_phone: user.user_metadata?.phone
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('You have already applied for this job');
      }
      throw error;
    }

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const emailResponse = await fetch(`${supabaseUrl}/functions/v1/send-job-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jobId: applicationData.jobId,
          name: user.user_metadata?.name || user.email?.split('@')[0] || 'Applicant',
          email: user.email,
          phone: user.user_metadata?.phone || '',
          message: applicationData.message,
          resumeUrl: resumeUrl,
          voiceIntroUrl: applicationData.voiceIntroUrl,
          videoUrl: videoUrl
        })
      });

      if (!emailResponse.ok) {
        console.warn('Email notification failed, but application was saved');
      }
    } catch (emailError) {
      console.warn('Failed to send email notification:', emailError);
    }

    return { application: convertSupabaseToApplication(data), error: null };
  } catch (error) {
    return { application: null, error: error as Error };
  }
}

export async function getJobApplications(jobId: string) {
  try {
    const { data, error } = await supabase
      .from('job_applications')
      .select('*')
      .eq('job_id', jobId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    const applications = (data || []).map(convertSupabaseToApplication);
    
    return { applications, error: null };
  } catch (error) {
    return { applications: [], error: error as Error };
  }
}

export async function getUserApplications() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('job_applications')
      .select('*')
      .eq('applicant_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    const applications = (data || []).map(convertSupabaseToApplication);
    return { applications, error: null };
  } catch (error) {
    return { applications: [], error: error as Error };
  }
}

export async function updateJobApplicationStatus(
  applicationId: string,
  updateData: UpdateJobApplicationData
) {
  try {
    const { data, error } = await supabase
      .from('job_applications')
      .update({
        status: updateData.status,
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .select()
      .single();

    if (error) throw error;

    if (updateData.status === 'accepted' || updateData.status === 'rejected') {
      try {
        const { data: jobData } = await supabase
          .from('jobs')
          .select('title')
          .eq('id', data.job_id)
          .maybeSingle();

        const jobTitle = jobData?.title || 'a job';
        const isAccepted = updateData.status === 'accepted';

        await supabase.from('notifications').insert({
          user_id: data.applicant_id,
          title: isAccepted ? 'Application Accepted' : 'Application Update',
          message: isAccepted
            ? `Your application for "${jobTitle}" has been accepted! The employer is interested in moving forward.`
            : `Your application for "${jobTitle}" was not selected. Keep applying - the right opportunity is out there.`,
          type: 'job_alert',
          reference_id: data.job_id,
          reference_type: 'job',
        });
      } catch (notifErr) {
        console.warn('Failed to create status notification:', notifErr);
      }
    }

    return { application: convertSupabaseToApplication(data), error: null };
  } catch (error) {
    return { application: null, error: error as Error };
  }
}

export async function withdrawJobApplication(applicationId: string) {
  try {
    const { data, error } = await supabase
      .from('job_applications')
      .update({
        status: 'withdrawn',
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .select()
      .single();

    if (error) throw error;

    return { application: convertSupabaseToApplication(data), error: null };
  } catch (error) {
    return { application: null, error: error as Error };
  }
}

export async function updateJobApplicationNotes(applicationId: string, notes: string) {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('job_applications')
      .update({
        notes,
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .eq('applicant_id', user.id)
      .select()
      .single();

    if (error) throw error;

    return { application: convertSupabaseToApplication(data), error: null };
  } catch (error) {
    return { application: null, error: error as Error };
  }
}

export async function getJobApplicationCounts(jobIds: string[]): Promise<Record<string, number>> {
  try {
    if (jobIds.length === 0) {
      return {};
    }

    const { data, error } = await supabase
      .from('job_applications')
      .select('job_id')
      .in('job_id', jobIds);

    if (error) throw error;

    const counts: Record<string, number> = {};
    jobIds.forEach(id => counts[id] = 0);

    (data || []).forEach(app => {
      counts[app.job_id] = (counts[app.job_id] || 0) + 1;
    });

    return counts;
  } catch (error) {
    return {};
  }
}